import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Dialog, VisuallyHidden } from '@buffer-mono/popcorn'

import { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'
import { getReturnSearchOnDialogClose } from '~publish/components/PostDialogs/helpers'
import { communityPage } from '~publish/legacy/routes'

import { buildCommentDetailsByPostRoute } from '../Comments/CommentsByPost/utils'
import { useCommentThread } from '../hooks/comments/useCommentThread'
import { usePersistedView } from '../hooks/usePersistedViewPreference'
import { CommentDetailsContent } from './CommentDetailsContent'
import { CommentErrorState } from './CommentErrorState'
import type { CommentThreadData } from './types'

import styles from './CommentDetailsDialog.module.css'

type FetchState =
  | { status: 'loading' }
  | { status: 'loaded'; commentId: string; thread: CommentThreadData }
  | { status: 'error'; errorMessage: string }

/**
 * The single-comment details dialog, mounted on the community
 * `/comment/:commentId` route and on the same path under `/by-post/:postId`.
 * This is the landing surface for shared comment links (see
 * copyCommentLink.ts) — it opens over the Community page from a pasted URL,
 * showing the comment with its post, ancestors, and replies.
 */
export function CommentDetailsDialog(): JSX.Element | null {
  const navigate = useNavigate()
  const location = useLocation()
  const { commentId, postId: selectedPostId } = useParams<{
    commentId: string
    postId?: string
  }>()
  const persistedView = usePersistedView()

  const [open, setOpen] = useState(true)
  const [fetchState, setFetchState] = useState<FetchState>({
    status: 'loading',
  })
  const { getCommentThread } = useCommentThread()
  const { channels, loading: channelsLoading } = useCommentsChannels()

  // Preserve the parent view's query state (filters, status) when
  // navigating back, dropping only dialog-scoped params.
  const returnSearch = getReturnSearchOnDialogClose(location.search)

  useEffect(() => {
    if (commentId === undefined) return

    // Reset when navigating between comment links (back/forward), so the
    // previous thread — and the Copy link's previous comment id — never
    // outlive their comment.
    setFetchState({ status: 'loading' })

    let cancelled = false
    getCommentThread(commentId)
      .then((thread) => {
        if (cancelled) return undefined
        setFetchState({ status: 'loaded', commentId, thread })
        return undefined
      })
      .catch((error: unknown) => {
        if (cancelled) return undefined
        setFetchState({
          status: 'error',
          errorMessage:
            error instanceof Error ? error.message : 'Unknown error',
        })
        return undefined
      })

    return (): void => {
      cancelled = true
    }
  }, [commentId, getCommentThread])

  // Over the by-post view, the comment's post is selected behind the dialog so
  // the reader sees it while the dialog is open and lands on it when the
  // dialog closes. The URL moves under the by-post route once the thread says
  // which post that is; the copied link stays the plain comment route. The
  // thread must be this comment's: moving between comment links keeps the
  // dialog mounted, so the previous thread is still in state on first render.
  const loadedPostId =
    fetchState.status === 'loaded' && fetchState.commentId === commentId
      ? fetchState.thread.originalComment?.post?.id
      : undefined
  useEffect(() => {
    if (commentId === undefined || selectedPostId !== undefined) return
    if (persistedView !== 'posts' || loadedPostId === undefined) return
    navigate(
      `${buildCommentDetailsByPostRoute({ postId: loadedPostId, commentId })}${location.search}`,
      { replace: true },
    )
  }, [
    commentId,
    selectedPostId,
    persistedView,
    loadedPostId,
    navigate,
    location.search,
  ])

  // `..` is route-relative and would leave the by-post form of the URL at
  // `/community`, so over the by-post view the close target names the post.
  const closeTarget =
    selectedPostId === undefined
      ? '..'
      : communityPage.getViewRoute({
          view: 'posts',
          postId: selectedPostId,
          copiedSearchParams: [],
        })

  const onOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen)
    if (!nextOpen) {
      navigate(`${closeTarget}${returnSearch}`)
    }
  }

  if (fetchState.status === 'loading' || channelsLoading) {
    return null
  }

  const originalComment =
    fetchState.status === 'loaded' ? fetchState.thread.originalComment : null
  const channel = channels.find(({ id }) => id === originalComment?.channelId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large" className={styles.content}>
        <VisuallyHidden>
          <Dialog.Title>Comment details</Dialog.Title>
        </VisuallyHidden>
        <Dialog.CloseButton />
        <Dialog.Body className={styles.body}>
          {fetchState.status === 'error' ? (
            <CommentErrorState errorMessage={fetchState.errorMessage} />
          ) : originalComment === null || channel === undefined ? (
            <CommentErrorState />
          ) : (
            <CommentDetailsContent
              originalComment={originalComment}
              channel={channel}
            />
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
